import { getShowUserCreation, getUserCreationStep } from '../../../../../../Drivers.selectors';
import { getLocale } from '../../../../../../../configuration/lang/langSlice';
import type { CreateDialogStepName, Driver, User } from '../../../../../../../types';
import { CreateUserDialog } from '../components/CreateUserDialog';
import { getSelectedDriverRtk, saveDriverSuccess, setSelectedDriver } from '../../../sidebarSlice';
import { createUserGoToStep, createUserToggleDialog } from '../userSlice';
import { useAppDispatch, useAppSelector } from '../../../../../../../configuration/setup/hooks';
import { inviteUserFailed, inviteUserRequested, inviteUserSuccess } from '../../../../../appSlice';
import { showErrorNotification, showSuccessNotification } from '../../../../../notifications/notifications';
import { useInviteUserMutation, useLazyGetGroupsQuery } from '../../../../../services/usersApi';
import { useLazyGetDriverQuery, useSaveDriverMutation } from '../../../../../services/driversApi';
import { FLEET_DRIVER_GROUP } from '../../../../../schema';
import { reportErrorToSentry } from '../../../../../../../configuration/setup/sentry';
import { ErrorOrigin } from '../../../../../services/apiUtils';
import { getApiErrorCode, isApiErrorWithCode } from '../../../../../services/apiErrors';

export interface CreateUserDialogPropertiesFromState {
    showDialog: boolean;
    currentStep: CreateDialogStepName;
    selectedDriver: Driver | null;
    preferredLocale: string | undefined;
}

export interface CreateUserDialogPropertiesFromDispatch {
    goToStep: (stepName: CreateDialogStepName) => void;
    closeCreateUserDialog: () => void;
    createUserAndSaveDriver: (user: User, selectedDriver: Driver) => void;
    saveSelectedDriver: (driver: Driver) => void;
}

export const CreateUserDialogContainer = () => {
    const dispatch = useAppDispatch();

    const showDialog = useAppSelector(getShowUserCreation);
    const currentStep = useAppSelector(getUserCreationStep);
    const selectedDriver = useAppSelector(getSelectedDriverRtk);
    const preferredLocale = useAppSelector(getLocale);

    const [inviteUserMutation] = useInviteUserMutation();
    const [loadGroups] = useLazyGetGroupsQuery();
    const [saveDriverMutation] = useSaveDriverMutation();
    const [loadDriver] = useLazyGetDriverQuery();

    const persistDriver = async (driver: Driver) => {
        await saveDriverMutation(driver).unwrap();
        const refreshedDriver = await loadDriver(driver.driverId, true).unwrap();
        dispatch(saveDriverSuccess(refreshedDriver));
        dispatch(setSelectedDriver(refreshedDriver));
        showSuccessNotification('intl-msg:save.success');
    };

    const createUserAndSaveDriver = async (user: User, currentDriver: Driver) => {
        dispatch(createUserToggleDialog());
        dispatch(inviteUserRequested());

        try {
            const groups = await loadGroups(undefined, true).unwrap();
            const driverGroup = groups.find(group => group.name === FLEET_DRIVER_GROUP);

            if (!driverGroup) {
                throw new Error('Fleet driver group missing.');
            }

            const invitedUser = await inviteUserMutation({
                ...user,
                groupIds: [driverGroup.id],
            }).unwrap();

            showSuccessNotification('intl-msg:users.invite.invitationSent');
            dispatch(inviteUserSuccess());

            await persistDriver({
                ...currentDriver,
                subject: invitedUser.subject,
            });
        } catch (error) {
            dispatch(inviteUserFailed());

            if (isApiErrorWithCode(error) && (error.status === 401 || error.status === 403)) {
                showErrorNotification('intl-msg:error.invite.unauthorized', true);
                return;
            }

            if (isApiErrorWithCode(error) && (error.status === 409 || error.detail === 'User already exists')) {
                showErrorNotification('intl-msg.error.invite.alreadyExists', true);
                return;
            }

            if (isApiErrorWithCode(error)) {
                showErrorNotification(getApiErrorCode(error, ErrorOrigin.USER_ADMINISTRATION));
                return;
            }

            reportErrorToSentry(
                new Error(
                    `Could not invite user ${user.id} for ${currentDriver.driverId} in account ${currentDriver.accountId}.`
                )
            );
            showErrorNotification('intl-msg:users.invite.error.invitationNotSent', true);
        }
    };

    const saveSelectedDriver = async (driver: Driver) => {
        dispatch(createUserToggleDialog());

        try {
            await persistDriver(driver);
        } catch (error) {
            if (isApiErrorWithCode(error)) {
                showErrorNotification(getApiErrorCode(error, ErrorOrigin.DRIVER_ADMINISTRATION));
            }
        }
    };

    return (
        <CreateUserDialog
            showDialog={showDialog}
            currentStep={currentStep}
            selectedDriver={selectedDriver}
            preferredLocale={preferredLocale}
            goToStep={stepName => {
                dispatch(createUserGoToStep(stepName));
            }}
            closeCreateUserDialog={() => {
                dispatch(createUserToggleDialog());
            }}
            createUserAndSaveDriver={createUserAndSaveDriver}
            saveSelectedDriver={saveSelectedDriver}
        />
    );
};
